import React, { useState, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { StyledAddActionButton } from './styled';
import { getLrProjectsError, EMPTY_ARRAY, addLrProjectError, getLrProceduresError, StyledModalHeaderIcon } from '@/shared';
import { handleFetchError, getAddButtonEnabled } from '@/helpers';
import { projectActions, projectSelectors, projectEntryActions } from '@/store';
import { Grid } from '@mantine/core';
import { Button } from 'antd';
import { Dropdown } from '@/components/Dropdown';
import { ProcedureCheckboxes } from '@/components/Procedure';

import { StyledModal, StyledInlineErrorMessage } from './styled';
import '../../../styles/app.css'

const AddLabRequest = ({
  onAddLab,
  localEntries
}) => {
  const dispatch = useDispatch();

  const affectedWeek = useSelector(state => state.projectEntry.projectEntries.selectedDays[0]);
  const oid = useSelector(state => state.user.activeUser?.oid);
  const projects = useSelector(state => projectSelectors.filteredProjects(state));
  const procedures = useSelector(state => state.projects.lrs.procedures);
  
  const [isLoading, setIsLoading] = useState(false);
  const [lab, setLab] = useState(null);
  const [selectedValues, setSelectedValues] = useState(new Set());
  const [inlineErrorMessage, setInlineErrorMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isAddButtonAvailable, setIsAddButtonAvailable] = useState(false);
  const systemTitle = 'Add LR';

  useEffect(() => {
    setIsAddButtonAvailable(getAddButtonEnabled(affectedWeek));
    if (!modalOpen) return;

    setIsLoading(true);
    dispatch(projectActions.getAllLrsProjects())
      .catch((e) => handleFetchError(e, getLrProjectsError))
      .finally(() => setIsLoading(false));
  }, [modalOpen]);

  useEffect(() => {
    if (lab?.requestNumber && !procedures[lab.requestNumber]) {
      dispatch(projectActions.getLrsProcedures(lab.requestNumber, lab.isNewLR))
        .catch((e) => handleFetchError(e, getLrProceduresError));
    }
  }, [lab, procedures]);

  const onProjectChange = (newProject) => {
    setLab(newProject);
    if (newProject?.requestNumber) {
      dispatch(projectActions.getLrsProcedures(newProject.requestNumber, newProject.isNewLR))
        .catch((e) => handleFetchError(e, getLrProceduresError));
    }
  };

  const proceduresForCurrentProject = useMemo(() => {
    if (!lab?.requestNumber || !procedures[lab.requestNumber]) return { data: EMPTY_ARRAY };
    return procedures[lab.requestNumber];
  }, [lab, procedures]);

  const sortedProceduresForCurrentProject = useMemo(() => {
    const selectedEntry = localEntries.find(x => x.requestId === lab?.requestId);
    var filteredProcedures = proceduresForCurrentProject.data.filter(p => {
      // Filter out procedures that are already in localEntries
      return !selectedEntry?.procedures?.some(entry => entry.procedureId === p.procedureIdLegacy);
    });

    return {
      ...proceduresForCurrentProject,
      data: filteredProcedures.sort((a, b) => a.procedureNumber - b.procedureNumber)
    }
  }, [proceduresForCurrentProject]);

  // This is not working as intended even with flag off...
  // const isAddBtnDisabled = !isArrayWithLength(newSortedProceduresForCurrentProject.data);

  const handleClose = () => {
    setLab(null);
    setSelectedValues(new Set());
    setIsLoading(false);
    setModalOpen(false);
  };

  const handleSubmit = async () => {
    if (selectedValues.size === 0) {
      setInlineErrorMessage('Select at least one procedure.');
      return;
    }
    setInlineErrorMessage(null);

    setIsLoading(true);
    
    const payload = {
      oid,
      affectedWeek,
      action: 'ADD',
      selectedProject: {
        ...lab,
        procedures: Array.from(selectedValues),
        type: 'LRS',
      },
    };

    try {
      //This still needs to save the procedure to the cosmos db since we don't have another way to pull info as of yet
      await dispatch(projectEntryActions.addTask(payload, 'LRS'));
      onAddLab(payload);
      handleClose();
    } catch (e) {
      handleFetchError(e, addLrProjectError);
      setIsLoading(false);
      handleClose();
    }
  };

  const getRequestDisplay = (requestNumber, isNewLR) => {
    return isNewLR ? `GRS LR - ${requestNumber}` : `LRS-${requestNumber}`;
  }
  return (
    <Fragment>
      <StyledAddActionButton disabled={!isAddButtonAvailable} onClick={() => setModalOpen(true)} light>
        <FontAwesomeIcon icon="plus" />{systemTitle}
      </StyledAddActionButton>
      <StyledModal size="md" centered isOpen={modalOpen}>
        <ModalHeader toggle={handleClose} disabled={isLoading}>
          <StyledModalHeaderIcon icon="plus" />{systemTitle}
        </ModalHeader>
        <ModalBody>
          <Grid>
            <Grid.Col>
              <Dropdown
                options={projects}
                value={lab}
                placeholder={'Search...'}
                onChange={onProjectChange}
                windowMode={true}
                getOptionValue={option => option.requestNumber}
                getOptionLabel={option => getRequestDisplay(option?.requestNumber, option?.isNewLR)}
                optionKey={'id'}
              />
            </Grid.Col>
          </Grid>
          {
            lab && (
              <ProcedureCheckboxes
                project={lab}
                procedures={sortedProceduresForCurrentProject}
                onSelectProcedure={setSelectedValues}
              />
            )
          }
          {
            inlineErrorMessage && (
              <StyledInlineErrorMessage>{inlineErrorMessage}</StyledInlineErrorMessage>
            )
          }
        </ModalBody>
        <ModalFooter>
          <Button className="secondary-antd-button" primary outline onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            className="primary-antd-button"
            primary
            onClick={() => handleSubmit()}
            loading={isLoading}
          >
            {systemTitle}
          </Button>
        </ModalFooter>
      </StyledModal>
    </Fragment>
  );
};

AddLabRequest.propTypes = {
  onAddLab: PropTypes.func
}

export default AddLabRequest;
